import { Form, Input } from "antd";
import { InfoPanelWrapper } from "./styles";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { modifyConfig, setJobPanelsComplete, updateJobPanelsComplete } from "redux/slices/user";
import { useEffect } from "react";

const InfoPanel = () => {
  const dispatch = useAppDispatch();
  const app = useAppSelector(state => state.app)
  const user = useAppSelector(state => state.user)
  const modelInfo = user.models[user.modelIndex];

  const { TextArea } = Input;

  const [aliasForm] = Form.useForm();
  const [descriptionForm] = Form.useForm();
  const forms = [aliasForm, descriptionForm];

  useEffect(() => {
    // Seed default alias/description whenever meta is missing them. Subtle
    // bug being defended against: useEffect fires bottom-up (children before
    // parents), so on initial Run-page mount InfoPanel's seed runs FIRST,
    // then the parent Run page's mount effect dispatches
    // `setConfig({reset:true})` which wipes meta back to `{}`. With a [] dep
    // array the seed never re-fires and the alias/description show up as
    // undefined in the eventual jobCreator/modelInitializer payloads. Keying
    // off `meta?.alias` makes the seed fire (a) on initial mount, (b) again
    // after the parent's reset, and (c) again whenever the user explicitly
    // clears the alias field — without overwriting a value the user typed.
    if (user.config && !user.config.meta?.alias) {
      dispatch(modifyConfig({
        type: "meta",
        value: {
          'alias': modelInfo.alias + '-' + new Date().toISOString(),
          'description': user.config.meta?.description ?? ''
        }
      }))
    }
  }, [user.config?.meta?.alias, modelInfo.alias, dispatch, user.config])

  useEffect(() => {
    if (app.submitting) {
      let promises: any[] = []

      for (let form of forms) {
        promises.push(form.validateFields().catch(() => {
          dispatch(setJobPanelsComplete({ jobsPanelsComplete: undefined }))
        }))
      }

      Promise.all(promises).then(() => {
        dispatch(updateJobPanelsComplete({ panel: 'meta', complete: true }))
      });
    }
  }, [app.submitting])

  const handleAliasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const meta = { ...user.config.meta };
    if (e.target.value === '') {
      meta['alias'] = modelInfo.alias + '-' + new Date().toISOString();
    } else {
      meta['alias'] = e.target.value;
    }
    dispatch(modifyConfig({ type: "meta", value: meta }));
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const meta = { ...user.config.meta };
    meta['description'] = e.target.value;
    dispatch(modifyConfig({ type: "meta", value: meta }));
  };

  return (
    <InfoPanelWrapper>
      <div className="inline-pill">
        <div className="inline-pill-alias-input">
          <Form
            form={aliasForm}
            autoComplete="off"
            requiredMark={false}
            className="inline-form"
          >
            <Form.Item
              name="alias"
              rules={[
                { min: 0, max: 64, message: 'Name must be 64 characters or less' },
                { pattern: /^[a-zA-Z0-9 ._-]*$/, message: 'Name can only include a-Z, 0-9, periods, underscores, and dashes' }
              ]}
              noStyle
            >
              <Input
                placeholder="Name this run..."
                className="inline-alias"
                size="small"
                onChange={handleAliasChange}
                disabled={app.submitting}
              />
            </Form.Item>
          </Form>
        </div>
        <div className="inline-pill-desc">
          <Form
            form={descriptionForm}
            autoComplete="off"
            requiredMark={false}
            className="inline-form"
          >
            <Form.Item
              name="description"
              rules={[
                { min: 0, max: 1000, message: '1000 chars max' },
                { pattern: /^[a-zA-Z0-9 .,\t\n\-_]*$/, message: 'letters, numbers, .,_-' }
              ]}
              noStyle
            >
              <TextArea
                rows={3}
                placeholder="Optional description for this run..."
                onChange={handleDescriptionChange}
                disabled={app.submitting}
              />
            </Form.Item>
          </Form>
        </div>
      </div>
    </InfoPanelWrapper>
  );
};

export default InfoPanel;
